// src/App.tsx
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import GlobalStyles from './styles/GlobalStyles';
import { theme } from './styles/theme';
import Navbar from './components/common/Navbar/Navbar';
import Footer from './components/common/Footer/Footer';
import ScrollProgress from './components/ui/ScrollProgress/ScrollProgress';
import PageTransition from './components/ui/PageTransition/PageTransition';
import styled from 'styled-components';
import ScrollToTop from './components/ui/ScrollToTop/ScrollToTop';
import CookieConsent from './components/ui/CookieConsent/CookieConsent';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Rooms = lazy(() => import('./pages/Rooms'));
const Amenities = lazy(() => import('./pages/Amenities'));
const Attractions = lazy(() => import('./pages/Attractions'));
const Contact = lazy(() => import('./pages/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/index'));
const TermsConditions = lazy(() => import('./pages/TermsConditions/index'));

// Loading Component
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: ${props => props.theme.colors.light};
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid ${props => props.theme.colors.gray[200]};
  border-top: 4px solid ${props => props.theme.colors.secondary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Loading = () => (
  <LoadingContainer>
    <LoadingSpinner />
  </LoadingContainer>
);

// Animated Routes Component
const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <PageTransition>
              <Home />
            </PageTransition>
          }
        />
        <Route
          path="/about"
          element={
            <PageTransition>
              <About />
            </PageTransition>
          }
        />
        <Route
          path="/rooms"
          element={
            <PageTransition>
              <Rooms />
            </PageTransition>
          }
        />
        <Route
          path="/amenities"
          element={
            <PageTransition>
              <Amenities />
            </PageTransition>
          }
        />
        <Route
          path="/attractions"
          element={
            <PageTransition>
              <Attractions />
            </PageTransition>
          }
        />
        <Route
          path="/contact"
          element={
            <PageTransition>
              <Contact />
            </PageTransition>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <PageTransition>
              <PrivacyPolicy />
            </PageTransition>
          }
        />
        <Route
          path="/terms-conditions"
          element={
            <PageTransition>
              <TermsConditions />
            </PageTransition>
          }
        />
        {/* 404 Catch-all Route - MUST BE LAST */}
        <Route
          path="*"
          element={
            <PageTransition>
              <NotFound />
            </PageTransition>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Router>
        <ScrollToTop />
        <ScrollProgress />
        <Navbar />
        <Suspense fallback={<Loading />}>
          <AnimatedRoutes />
        </Suspense>
        <Footer />
        <CookieConsent />
      </Router>
    </ThemeProvider>
  );
};

export default App;